import { useCallback } from 'react';

// Error severity levels
export const ERROR_SEVERITY = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
  CRITICAL: 'CRITICAL',
};

// Error categories for better organization
export const ERROR_CATEGORY = {
  NETWORK: 'NETWORK',
  DATABASE: 'DATABASE',
  AUTH: 'AUTH',
  PROJECT: 'PROJECT',
  UI: 'UI',
  VALIDATION: 'VALIDATION',
};

class ErrorLogger {
  constructor() {
    this.logs = [];
    this.errorCallbacks = new Set();
  }

  // Add callback for error notifications
  addErrorCallback(callback) {
    this.errorCallbacks.add(callback);
  }

  // Remove callback
  removeErrorCallback(callback) {
    this.errorCallbacks.delete(callback);
  }

  // Main logging method
  log(error, metadata = {}) {
    const errorLog = this.createErrorLog(error, metadata);
    this.logs.push(errorLog);

    // Notify all registered callbacks
    this.errorCallbacks.forEach(callback => callback(errorLog));

    // Always console.error in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error Log:', errorLog);
    }

    return errorLog;
  }

  // Create structured error log
  createErrorLog(error, metadata = {}) {
    const timestamp = new Date().toISOString();
    const errorMessage = error instanceof Error ? error.message : String(error);

    return {
      timestamp,
      message: errorMessage,
      stack: error instanceof Error ? error.stack : undefined,
      severity: metadata.severity || ERROR_SEVERITY.ERROR,
      category: metadata.category || ERROR_CATEGORY.UI,
      metadata: {
        ...metadata,
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
      id: `${timestamp}-${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  // Get all logs
  getLogs() {
    return [...this.logs];
  }

  // Get logs by severity
  getLogsBySeverity(severity) {
    return this.logs.filter(log => log.severity === severity);
  }

  // Get logs by category
  getLogsByCategory(category) {
    return this.logs.filter(log => log.category === category);
  }

  // Clear all logs
  clearLogs() {
    this.logs = [];
  }
}

// Create singleton instance
export const errorLogger = new ErrorLogger();

// Custom error types
export class ProjectError extends Error {
  constructor(message, metadata = {}) {
    super(message);
    this.name = 'ProjectError';
    this.metadata = metadata;
  }
}

// Hook for components to use error logging
export const useErrorLogging = () => {
  const logError = useCallback((error, metadata = {}) => {
    return errorLogger.log(error, metadata);
  }, []);

  return { logError };
};
